import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import Profile from './views/Profile';
import Login from './views/Login';
import MainLayout from './components/MainLayout';
import AuthLayout from './components/AuthLayout';
import SystemsList from './views/Systems';
import SystemDetailView from './views/Systems/SystemDetail';
import SystemForm from './views/Systems/SystemForm';
import PublicEntitiesList from './views/PublicEntities';
import PublicEntityDetailView from './views/PublicEntities/PublicEntityDetail';
import PublicEntityForm from './views/PublicEntities/PublicEntityForm';
import UserList from './views/Users';
import UserDetail from './views/Users/UserDetail';
import UserForm from './views/Users/UserForm';
import Dashboard from './views/Dashboard';
import ConfigPubList from './views/ConfigPublicacoes';
import ConfigPubDetail from './views/ConfigPublicacoes/ConfigPubDetail';
import ConfigPubForm from './views/ConfigPublicacoes/ConfigPubForm';
import AdminPublicationsList from './views/ActaPublicationsAdmin';
import AdminPublicationDetail from './views/ActaPublicationsAdmin/AdminPublicationDetail';
import AdminPublicationForm from './views/ActaPublicationsAdmin/AdminPublicationForm';
import SynthesisConfigurationsList from './views/SynthesisConfigurations';
import SynthesisConfigurationDetail from './views/SynthesisConfigurations/SynthesisConfigurationDetail';
import SynthesisConfigurationForm from './views/SynthesisConfigurations/SynthesisConfigurationForm';

import useAuth from './hooks/useAuth';
import { CircularProgress, Backdrop, Box, Typography } from '@mui/material';

function CustomRoute({
  isPrivate,
  element,
}: {
  isPrivate?: boolean;
  element: React.ReactElement;
}) {
  const { authenticated, loading } = useAuth();

  if (loading) {
    return (
      <Backdrop open={true} sx={{ zIndex: 1300 }}>
        <CircularProgress color="inherit" />
      </Backdrop>
    );
  }

  if (isPrivate && !authenticated) {
    return <Navigate to="/login" replace />;
  }

  return element;
}

const AppRoutes: React.FC = () => {
  const { authenticated, loading } = useAuth();

  if (loading) {
    return (
      <Backdrop open={true} sx={{ zIndex: 1300 }}>
        <CircularProgress color="inherit" />
      </Backdrop>
    );
  }

  return (
    <Routes>
      <Route
        path="/"
        element={
          authenticated ? (
            <Navigate to="/dashboard" replace />
          ) : (
            <Navigate to="/login" replace />
          )
        }
      />
      <Route
        path="/login"
        element={
          authenticated ? (
            <Navigate to="/dashboard" replace />
          ) : (
            <AuthLayout>
              <Login />
            </AuthLayout>
          )
        }
      />
      <Route
        path="/perfil"
        element={
          <CustomRoute
            isPrivate={true}
            element={
              <MainLayout>
                <Profile />
              </MainLayout>
            }
          />
        }
      />
      <Route
        path="/dashboard"
        element={
          <CustomRoute
            isPrivate={true}
            element={
              <MainLayout>
                <Dashboard />
              </MainLayout>
            }
          />
        }
      />

      {/* Rotas de Sistemas */}
      <Route
        path="/sistemas"
        element={
          <CustomRoute
            isPrivate={true}
            element={
              <MainLayout>
                <SystemsList />
              </MainLayout>
            }
          />
        }
      />
      <Route
        path="/sistemas/novo"
        element={
          <CustomRoute
            isPrivate={true}
            element={
              <MainLayout>
                <SystemForm />
              </MainLayout>
            }
          />
        }
      />
      <Route
        path="/sistemas/:systemId"
        element={
          <CustomRoute
            isPrivate={true}
            element={
              <MainLayout>
                <SystemDetailView />
              </MainLayout>
            }
          />
        }
      />
      <Route
        path="/sistemas/:systemId/editar"
        element={
          <CustomRoute
            isPrivate={true}
            element={
              <MainLayout>
                <SystemForm />
              </MainLayout>
            }
          />
        }
      />

      {/* Rotas de Entidades Públicas */}
      <Route
        path="/entidades-publicas"
        element={
          <CustomRoute
            isPrivate={true}
            element={
              <MainLayout>
                <PublicEntitiesList />
              </MainLayout>
            }
          />
        }
      />
      <Route
        path="/entidades-publicas/novo"
        element={
          <CustomRoute
            isPrivate={true}
            element={
              <MainLayout>
                <PublicEntityForm />
              </MainLayout>
            }
          />
        }
      />
      <Route
        path="/entidades-publicas/:entityId"
        element={
          <CustomRoute
            isPrivate={true}
            element={
              <MainLayout>
                <PublicEntityDetailView />
              </MainLayout>
            }
          />
        }
      />
      <Route
        path="/entidades-publicas/:entityId/editar"
        element={
          <CustomRoute
            isPrivate={true}
            element={
              <MainLayout>
                <PublicEntityForm />
              </MainLayout>
            }
          />
        }
      />

      {/* Rotas de Usuários */}
      <Route
        path="/usuarios"
        element={
          <CustomRoute
            isPrivate={true}
            element={
              <MainLayout>
                <UserList />
              </MainLayout>
            }
          />
        }
      />
      <Route
        path="/usuarios/novo"
        element={
          <CustomRoute
            isPrivate={true}
            element={
              <MainLayout>
                <UserForm />
              </MainLayout>
            }
          />
        }
      />
      <Route
        path="/usuarios/:userId"
        element={
          <CustomRoute
            isPrivate={true}
            element={
              <MainLayout>
                <UserDetail />
              </MainLayout>
            }
          />
        }
      />
      <Route
        path="/usuarios/:userId/editar"
        element={
          <CustomRoute
            isPrivate={true}
            element={
              <MainLayout>
                <UserForm />
              </MainLayout>
            }
          />
        }
      />

      {/* ================ Configurações de Publicações =============== */}
      <Route
        path="/configuracoes-publicacoes"
        element={
          <CustomRoute
            isPrivate={true}
            element={
              <MainLayout>
                <ConfigPubList />
              </MainLayout>
            }
          />
        }
      />
      <Route
        path="/configuracoes-publicacoes/novo"
        element={
          <CustomRoute
            isPrivate={true}
            element={
              <MainLayout>
                <ConfigPubForm />
              </MainLayout>
            }
          />
        }
      />
      <Route
        path="/configuracoes-publicacoes/:cfgId"
        element={
          <CustomRoute
            isPrivate={true}
            element={
              <MainLayout>
                <ConfigPubDetail />
              </MainLayout>
            }
          />
        }
      />
      <Route
        path="/configuracoes-publicacoes/:cfgId/editar"
        element={
          <CustomRoute
            isPrivate={true}
            element={
              <MainLayout>
                <ConfigPubForm />
              </MainLayout>
            }
          />
        }
      />
      {/* ============= Configurações de Publicações ============ */}

      {/* Rotas para Publicações Admin (Acta via Connect) */}
      <Route
        path="/publicacoes-admin"
        element={
          <CustomRoute
            isPrivate={true}
            element={
              <MainLayout>
                <AdminPublicationsList />
              </MainLayout>
            }
          />
        }
      />
      <Route
        path="/publicacoes-admin/novo"
        element={
          <CustomRoute
            isPrivate={true}
            element={
              <MainLayout>
                <AdminPublicationForm />
              </MainLayout>
            }
          />
        }
      />
      <Route
        path="/publicacoes-admin/:publicationId"
        element={
          <CustomRoute
            isPrivate={true}
            element={
              <MainLayout>
                <AdminPublicationDetail />
              </MainLayout>
            }
          />
        }
      />
      <Route
        path="/synthesis/configurations"
        element={
          <CustomRoute
            isPrivate={true}
            element={
              <MainLayout>
                <SynthesisConfigurationsList />
              </MainLayout>
            }
          />
        }
      />
      <Route
        path="/synthesis/configurations/novo"
        element={
          <CustomRoute
            isPrivate={true}
            element={
              <MainLayout>
                <SynthesisConfigurationForm />
              </MainLayout>
            }
          />
        }
      />
      <Route
        path="/synthesis/configurations/:cfgId"
        element={
          <CustomRoute
            isPrivate={true}
            element={
              <MainLayout>
                <SynthesisConfigurationDetail />
              </MainLayout>
            }
          />
        }
      />
      <Route
        path="/synthesis/configurations/:cfgId/editar"
        element={
          <CustomRoute
            isPrivate={true}
            element={
              <MainLayout>
                <SynthesisConfigurationForm />
              </MainLayout>
            }
          />
        }
      />

      {/* Rota para 404 */}
      <Route
        path="*"
        element={
          <MainLayout>
            <Box sx={{ textAlign: 'center', mt: 5 }}>
              <Typography variant="h4">404 - Página Não Encontrada</Typography>
              <Typography variant="body1" sx={{ mt: 2 }}>
                A página que você está procurando não existe.
              </Typography>
            </Box>
          </MainLayout>
        }
      />
    </Routes>
  );
};

export default AppRoutes;